/**
 * Route Configuration
 * 
 * This file defines the routing configuration for the frontend. Every route
 * now points at a migrated component; the legacy tree it used to reach into is
 * gone.
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4, 7.1, 10.1, 10.2, 10.3, 10.4, 10.5
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '../shared/utils/constants';
import { ErrorBoundary } from '../shared/components/ErrorBoundary';
import { PageLoader } from '../shared/components/layout/PageLoader';

// Migrated components from src/
// Workspace is the heaviest screen — it pulls the Ace editor, the syntax
// highlighter and the PDF/spreadsheet exporters. Deferring it keeps all of
// those chunks off the initial load; the file has a default export too, but
// the named one is what the rest of the app imports.
const Workspace = lazy(() => import('../pages/Workspace').then((m) => ({ default: m.Workspace })));

// Lazy-loaded migrated components
// Requirement 10.2: Configure lazy loading for Dashboard component
const Dashboard = lazy(() => import('../pages/Dashboard.jsx'));

// Migrated components from src/
import SignIn from '../pages/SignIn.jsx';
const OrganizationSignup = lazy(() => import('../pages/OrganizationSignup.jsx'));
const TestLogs = lazy(() => import('../pages/TestLogs.jsx'));
const Help = lazy(() => import('../pages/Help.jsx'));

const Environment_pg = lazy(() => import('../pages/EnvironmentPage'));
// Migrated Admin components from src/
const Admin = lazy(() => import('../pages/Admin.jsx'));
// Migrated Load Test components from src/
const LoadTest = lazy(() => import('../pages/LoadTest.jsx'));
const LoadTestDetails = lazy(() => import('../pages/LoadTestDetails.jsx'));

// Migrated Test Plans components from src/
const TestPlans = lazy(() => import('../pages/TestPlans.jsx'));
const ScheduledTestResults = lazy(() => import('../pages/test-plans/ScheduledTestResults.jsx'));

// Email deep-link landing page (forwards into existing results pages via router state)
const EmailResultRedirect = lazy(() => import('../pages/test-plans/EmailResultRedirect.jsx'));

// Migrated orchestration result screen
const OrchestrationTestResults = lazy(() => import('../pages/test-plans/OrchestrationTestResults.jsx'));
const AIGenerate = lazy(() => import('../pages/AIGenerate'));
import AcceptInvite from '../pages/AcceptInvite';
const OrganizationAdmin = lazy(() => import('../pages/OrganizationAdmin.jsx'));

import LandingPage from '../pages/LandingPage/LandingPage.jsx';
const SignUp = lazy(() => import('../pages/SignUp.jsx'));

const MockServers = lazy(() => import('../pages/MockServers.jsx'));
const ScheduleResultsPage = lazy(() => import('../features/advanced-orchestration/pages/ScheduleResultsPage.jsx'));
// Platform vision: new pages
const Workflow = lazy(() => import('../pages/Workflow.jsx'));
const WorkflowDetailPage = lazy(() => import('../pages/WorkflowDetailPage.jsx'));

// Platform vision: role guard

/**
 * Route definitions for the application
 * Migrated from src/Routes/routes.jsx
 * Requirements: 2.1, 2.2, 2.3, 2.4, 6.1, 6.2, 7.1, 10.1, 10.2, 10.3, 10.4, 10.5
 */
// Deferred screens share one boundary: a lazy chunk that fails to load shows the
// error boundary rather than a blank route, and PageLoader covers the fetch.
const deferred = (Component) => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </ErrorBoundary>
);

export const routes = [
  // Requirement 10.1: Dashboard route with path /dashboard
  // Requirement 10.2: Lazy-loaded Dashboard component
  // Requirement 10.3: Authentication protection (handled in AppContent)
  // Requirement 10.4: Error boundary included
  {
    path: ROUTES.DASHBOARD,
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Dashboard />
        </Suspense>
      </ErrorBoundary>
    ),
    name: 'Dashboard'
  },
  { path: ROUTES.SIGN_IN, element: <SignIn />, name: 'Sign In' },
  { path: ROUTES.SIGN_UP, element: deferred(SignUp), name: 'Sign Up' },
  { path: ROUTES.ORGANIZATION_SIGNUP, element: deferred(OrganizationSignup), name: 'Organization Signup' },
  { path: ROUTES.ACCEPT_INVITE, element: <AcceptInvite />, name: 'Accept Invite' },
  { path: ROUTES.ORGANIZATION_ADMIN, element: deferred(OrganizationAdmin), name: 'Organization Admin' },
  { path: ROUTES.ENVIRONMENT, element: deferred(Environment_pg), name: 'Environment' },
  // Migrated Admin routes - uses src/ component
  { path: ROUTES.ADMIN, element: deferred(Admin), name: 'Admin' },
  { path: ROUTES.REQUESTS, element: deferred(Admin), name: 'Requests' },
  { path: ROUTES.PROJECTS, element: deferred(Admin), name: 'Projects' },
  { path: ROUTES.USERS, element: deferred(Admin), name: 'Users' },
  { path: ROUTES.ROLES, element: deferred(Admin), name: 'Roles' },
  { path: ROUTES.AGENTS, element: deferred(Admin), name: 'Agents' },
  // Migrated route - uses src/ component directly (no WorkspaceBridge)
  // Requirements: 7.1, 7.2, 7.3
  { path: ROUTES.INPUTS, element: deferred(Workspace), name: 'Workspace' },
  { path: ROUTES.LOAD, element: deferred(LoadTest), name: 'Load Test' },
  { path: ROUTES.LOAD_DETAILS, element: deferred(LoadTestDetails), name: 'Load Test Details' },
  // Also expose a Test Logs specific details route so details opened from Test Logs
  { path: ROUTES.TEST_LOGS_LOAD_DETAILS, element: deferred(LoadTestDetails), name: 'Test Logs Load Details' },
  // Migrated Test Plans routes - uses src/ components
  { path: ROUTES.SCHEDULED_TEST_PLANS, element: deferred(TestPlans), name: 'Scheduled Test Plans' },
  { path: ROUTES.SCHEDULED_TEST_RESULTS, element: deferred(ScheduledTestResults), name: 'Scheduled Test Results' },
  { path: ROUTES.SCHEDULED_TEST_RESULTS_OPEN, element: deferred(EmailResultRedirect), name: 'Open Test Result From Email' },
  { path: ROUTES.TEST_LOGS, element: deferred(TestLogs), name: 'Test Logs' },
  { path: ROUTES.HELP, element: deferred(Help), name: 'Help' },
  { path: ROUTES.AI_GENERATE, element: deferred(AIGenerate), name: 'AI Generate' },
  { path: ROUTES.MOCK_SERVERS, element: deferred(MockServers), name: 'Mock Servers' },
  // Platform vision: Workflow and role-specific workspaces
  {
    path: ROUTES.WORKFLOW,
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Workflow />
        </Suspense>
      </ErrorBoundary>
    ),
    name: 'Workflow'
  },
  {
    path: ROUTES.WORKFLOW_DETAIL,
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <WorkflowDetailPage />
        </Suspense>
      </ErrorBoundary>
    ),
    name: 'Workflow Detail'
  },
  {
    path: '/',
    element: <LandingPage />,
    name: 'Home'
  },
  { path: '/orchestration-load-result/view', element: deferred(OrchestrationTestResults), name: 'Orchestration Load Result' },
  { path: '/advanced-orchestration/schedule-results', element: deferred(ScheduleResultsPage), name: 'Advanced Orchestration Schedule Results' },
];

/**
 * AppRoutes component that renders the route configuration
 * This is used when src/App.jsx is rendered as the main entry point
 * Requirements: 2.2, 6.1, 6.2
 */
function AppRoutes() {
  return (
    <Routes>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={route.element}
        />
      ))}
      {/* Fallback route - redirect to dashboard */}
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}

export default AppRoutes;
